/**
 * EditViewItem.js
 *
 * Draggable list item used in view editor interfaces.
 * Provides primary and secondary labels, and supports reordering via react-dnd.
 */

import { memo } from "react";
import PropTypes from "prop-types";
import { useDrag, useDrop } from "react-dnd";
import { FormattedMessage } from "react-intl";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { default as ProjectViewUtils } from "../../ProjectViews/projectViewUtils";
import DragHandle from "../../common/DragHandle";


/**
 * Internal drag-and-drop item type used with react-dnd.
 * Only relevant within this component.
 * @readonly
 * @enum {string}
 */
const ItemTypes = {
  VIEW: 'VIEW',
};


/**
 * Generates display labels for a view item.
 *
 * If `name` exists, it's used as the primary label (`nameLabel`),
 * and the translated label (based on type) becomes the secondary (`typeLabel`).
 * If `name` is missing, only the translated label is returned as `nameLabel`.
 *
 * @param {ViewItem} viewItem - The view item data
 * @returns {{ nameLabel: string | JSX.Element, typeLabel?: string | JSX.Element }}
 */
const createLabels = (viewItem) => {
  const { name, type, viewId } = viewItem;

  const key = ProjectViewUtils.getTranslationKey(type);

  const translatedLabel = key
    ? <FormattedMessage id={key} />
    : `(${viewId})`;

  const hasName = !!name?.length;

  return {
    nameLabel: hasName ? name : translatedLabel,
    typeLabel: hasName ? translatedLabel : undefined,
  };
};


/**
 * @typedef ViewItem
 * @property {string} name - Display name of the view item
 * @property {number} type - View type used for translations and logic
 * @property {number} viewId - Unique identifier for the view item
 */

/**
 * @callback OnMoveCallback
 * @param {number} fromIndex - The index from which the item was dragged
 * @param {number} toIndex - The index to which the item is dropped
 */

/**
 * @typedef EditViewItemProps
 * @property {number} index - Index of the item in the list
 * @property {boolean} isSelected - Whether this item is currently selected
 * @property {ViewItem} item - The view item data
 * @property {OnMoveCallback} [onMove] - Callback fired when the item is moved via drag-and-drop
 * @property {() => void} onSelect - Callback fired when the edit button is clicked
 */

/**
 * A draggable list item component.
 *
 * @param {EditViewItemProps} props
 * @returns {JSX.Element}
 */
const EditViewItem = ({
  index,
  isSelected,
  item,
  onMove,
  onSelect,
}) => {
  const { viewId } = item;

  const { nameLabel, typeLabel } = createLabels(item);

  const [{ isDragging }, dragRef] = useDrag({
    type: ItemTypes.VIEW,
    item: { index, viewId }, // Initial drag data: index and identifier
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: ItemTypes.VIEW,
    hover: (draggedItem, monitor) => {
      // Only handle hover for the top-level item (avoid nested children)
      if (monitor.isOver({ shallow: true })) {
        return;
      }

      // Ignore if dragging over the same item � no reordering needed
      if (draggedItem.viewId === viewId) {
        return;
      }

      // Ignore if index hasn't changed (safety check)
      if (draggedItem.index === index) {
        return;
      }

      if (onMove) {
        // Save the original index before the move
        const fromIndex = draggedItem.index;

        // Trigger parent's callback to update the item order in state
        onMove(fromIndex, index);

        /**
         * IMPORTANT:
         * We must update the dragged item's index to the new position.
         * 
         * This mutation informs react-dnd of the updated drag state,
         * ensuring it tracks the item at the new index during subsequent events.
         * 
         * Without this, react-dnd would think the item is still at the old index,
         * causing flickering or incorrect reordering behavior.
         */
        draggedItem.index = index;
      }
    },
  });

  const listItemStyle = {
    ...(isSelected && {
      backgroundColor: "action.selected",
    }),
    ...(isDragging && {
      backgroundColor: "action.hover",
    }),
    "& .MuiListItemText-root": {
      alignItems: "center",
      display: "flex",
      fontSize: "smaller",
      my: 0,
    },
    "& .MuiListItemText-primary": {
      mr: 0.5,
      flexGrow: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiListItemText-secondary": {
      fontSize: "x-small",
      whiteSpace: "nowrap",
    },
  };

  return (
    <ListItem
      divider
      ref={(node) => dropRef(node)}
      secondaryAction={
        <IconButton edge="end" aria-label="edit" onClick={onSelect}
          sx={{
            ...(isSelected && {
              visibility: "hidden",
            })
          }}
        >
          <EditOutlinedIcon />
        </IconButton>
      }
      sx={listItemStyle}
    >
      <ListItemIcon>
        <DragHandle ref={dragRef} />
      </ListItemIcon>
      <ListItemText
        primary={nameLabel}
        secondary={typeLabel}
      />
    </ListItem>
  );
};

export const ViewItemPropType = PropTypes.shape({
  name: PropTypes.string,
  type: PropTypes.number.isRequired,
  viewId: PropTypes.number.isRequired,
});

EditViewItem.propTypes = {
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
  item: ViewItemPropType.isRequired,
  onMove: PropTypes.func, // optional
  onSelect: PropTypes.func.isRequired,
};

EditViewItem.displayName = "EditViewItem";

export default memo(EditViewItem);
